import { ref, onMounted, onUnmounted } from 'vue'
import type { DocHeading } from '@/types/docs'

export function useScrollSpy(headings: DocHeading[]) {
  const activeId = ref<string | null>(null)
  let observer: IntersectionObserver | null = null

  function setup(): void {
    if (headings.length === 0) return

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    // When reduced motion is preferred, simply highlight the first heading
    if (prefersReducedMotion) {
      activeId.value = headings[0]?.id ?? null
      return
    }

    const headingEls: Element[] = headings
      .map((h) => document.getElementById(h.id))
      .filter((el): el is HTMLElement => el !== null)

    if (headingEls.length === 0) return

    // Track which headings are above the viewport fold
    const visibleMap = new Map<string, boolean>()

    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          visibleMap.set(entry.target.id, entry.isIntersecting)
        }

        // Find the first visible heading, or the last above the fold
        const firstVisible = headingEls.find((el) => visibleMap.get(el.id))
        if (firstVisible) {
          activeId.value = firstVisible.id
        }
      },
      {
        rootMargin: '0px 0px -60% 0px',
        threshold: 0,
      },
    )

    for (const el of headingEls) {
      observer.observe(el)
    }
  }

  onMounted(() => {
    // Defer until the DOM has painted with the article content
    requestAnimationFrame(setup)
  })

  onUnmounted(() => {
    observer?.disconnect()
    observer = null
  })

  return { activeId }
}
